import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { environment } from '../../environments/environment';

import { MAX_MERCATOR_LAT } from '../map/constants/constants';
import { CustomIconControl } from '../map/custom-controls/custom-icon.control';
import mapboxgl, {
  FullscreenControl,
  LngLatLike,
  NavigationControl,
} from 'mapbox-gl';
import moment, { Moment } from 'moment';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { onSelectLayer, setSavedMapLayerStyle } from 'src/utils/constants';

@Injectable({
  providedIn: 'root',
})
export class ModelMapService {
  private http = inject(HttpClient);
  private dialog = inject(MatDialog);

  map: mapboxgl.Map;
  localStorageId = 'model_map_service_map_layer';
  style = 'mapbox://styles/' + environment.mapbox.layers[0].style;
  zoom = 4;
  regions = {
    eu: {
      coordinates: [
        [-15.1242158, 59.8746959],
        [44.8756544, 59.8746959],
        [44.8756544, 29.8747792],
        [-15.1242158, 29.8747792],
      ],
      padding: 0,
    },
    de: {
      coordinates: [
        [1.7966306, 55.7054442],
        [22.4578821, 55.7054442],
        [22.4578821, 46.7416115],
        [1.7966306, 46.7416115],
      ],
      padding: 0,
    },
    es: {
      coordinates: [
        [-9.522142, 43.9001296],
        [3.5932611, 43.9001296],
        [3.5932611, 35.9954842],
        [-9.522142, 35.9954842],
      ],
      padding: 0,
    },
  };
  mapLayers = environment.mapbox.layers;

  modelData: ModelData;
  language: string;

  timeStampControl: TimeStampControl;
  modelLayers: Array<mapboxgl.AnyLayer> = [];

  runTimes = [];
  selectedRun: Moment;
  modelsId = {
    gfs: 1,
    ecmwf: 2,
    iconeu: 3,
    arome: 4,
    arpege: 5,
    uk2km: 6,
    uk10km: 7,
    icond2: 8,
    icon: 9,
  };
  runData = {};

  model: string;
  parameter: string;
  regionName: string;
  run: Moment;
  selectedTimeStep: Moment;

  private dataUrl = environment.url.dataArchiveProxy;

  constructor() {
    mapboxgl.accessToken = environment.mapbox.accessToken;
    setSavedMapLayerStyle.bind(
      this,
      this.localStorageId,
      this.mapLayers,
      (style: string) => {
        this.style = style;
      }
    )();
    // this.setSpanishRegionPointsByClient();
  }

  getModelData(
    model: string,
    parameter: string,
    regionName: string,
    selectedRun?: Moment
  ) {
    const today = moment()
      .set({ hour: 0, minute: 0, seconds: 0 })
      .toISOString();
    const startDtg = moment(today).subtract(5, 'days').toISOString();
    const endDtg = moment(today).add(16, 'days').toISOString();

    return this.getRunTimes(model, 'TMP2m', startDtg, endDtg).pipe(
      tap(
        (
          data: { runtime: string; validAt: { dtg: string; item: string }[] }[]
        ) => {
          const runData = data
            .slice()
            .sort((a, b) => {
              if (moment(a.runtime).isAfter(b.runtime)) {
                return -1;
              } else if (moment(b.runtime).isAfter(a.runtime)) {
                return 1;
              }
              return 0;
            })
            .slice(0, 8);

          this.runTimes = [];
          this.runData = {};

          for (const dataset of runData) {
            const run = moment(dataset.runtime).utc();
            if (run.hour() % 6 === 0) {
              this.runTimes.push(run);
              this.runData[run.toISOString()] = this.replaceDummyParameter(
                parameter,
                regionName,
                dataset.validAt
              );
            }
          }
          this.selectedRun = selectedRun ? selectedRun : this.runTimes[0];
        }
      ),
      map(() => {
        return {
          runTimes: this.runTimes,
          runData: this.runData,
        };
      })
    );
  }

  replaceDummyParameter(
    parameter: string,
    regionName: string,
    data: { dtg: string; item: string }[]
  ): any[] {
    const replacedData = [];
    const suffix = regionName === 'eu' ? '' : '_' + regionName;
    for (const dataset of data) {
      replacedData.push({
        dtg: dataset.dtg,
        item:
          dataset.item
            //.replace(
            //  /_\d{10}_/,
            //  '_' + moment(dataset.dtg).format('YYYYMMDD') + '_'
            //)
            .replace('TMP2m', parameter) + suffix,
      });
    }
    return replacedData;
  }

  buildMap(
    center: LngLatLike,
    model: string,
    parameter: string,
    regionName: string,
    run: Moment,
    selectedTimeStep: Moment
  ): void {
    this.setModelParameters(
      model,
      parameter,
      regionName,
      run,
      selectedTimeStep
    );

    const bounds = this.getBounds(regionName);

    this.map = new mapboxgl.Map({
      container: 'map',
      style: this.style,
      zoom: this.zoom,
      bounds,
      maxBounds: bounds,
      scrollZoom: true,
      cooperativeGestures: true,
      projection: { name: 'mercator' },
      // center,
    });
    this.map.on('load', () => {
      this.flyToRegion(regionName);

      this.map.addControl(new FullscreenControl());
      this.map.addControl(new NavigationControl());
      this.setSelectLayerControl();
      this.removeModelLayers();
      this.addModelLayers(model, parameter, regionName, run, selectedTimeStep);
    });
  }

  addCountryLayers(): void {
    if (!this.map.getSource('country-boundaries')) {
      this.map.addSource('country-boundaries', {
        type: 'vector',
        url: 'mapbox://mapbox.country-boundaries-v1',
      });
      this.map.addLayer({
        id: 'boundary-layer',
        source: 'country-boundaries',
        'source-layer': 'country_boundaries',
        type: 'line', // Wichtig: Typ auf 'line' ändern
        filter: ['==', ['get', 'disputed'], 'false'],
        paint: {
          'line-color': 'rgba(255, 255, 255, 0.9)',
          'line-width': 2, // Hier stellst du die Dicke ein (in Pixeln)
          'line-blur': 1, // Optional: Macht die Kanten etwas weicher
        },
      });
      this.map.addSource('streets', {
        url: 'mapbox://mapbox.mapbox-streets-v8',
        type: 'vector',
      });
      this.map.addLayer({
        id: 'country-names',
        source: 'streets',
        'source-layer': 'place_label',
        type: 'symbol',
        paint: {
          'text-color': '#FFFFFF',
          'text-opacity': 1.0,
          'text-halo-color': 'rgb(91,91,91)',
          'text-halo-width': 0.8,
          'text-halo-blur': 0.5,
        },
        layout: {
          'text-field': ['get', 'name_' + this.language],
        },
        filter: ['==', 'type', 'country'],
      });
      /*
      this.map.addLayer({
        id: 'road',
        source: 'streets',
        'source-layer': 'road',
        type: 'line',
        paint: {
          'line-color': '#ffffff',
        },
        // filter: ['==', 'type', 'motorway_link'],
      });

       */
      this.map.addLayer({
        minzoom: 3,
        layout: {
          'line-cap': ['step', ['zoom'], 'butt', 13, 'round'],
          'line-join': ['step', ['zoom'], 'miter', 13, 'round'],
        },
        filter: [
          'all',
          [
            'step',
            ['zoom'],
            ['match', ['get', 'class'], ['motorway', 'trunk'], true, false],
            5,
            [
              'all',
              ['match', ['get', 'class'], ['motorway', 'trunk'], true, false],
              ['match', ['get', 'structure'], ['none', 'ford'], true, false],
            ],
          ],
          ['==', ['geometry-type'], 'LineString'],
        ],
        type: 'line',
        source: 'streets',
        id: 'road-motorway-trunk-1',
        paint: {
          'line-width': [
            'interpolate',
            ['exponential', 1.5],
            ['zoom'],
            3,
            0.8,
            18,
            30,
            22,
            300,
          ],
          'line-color': [
            'step',
            ['zoom'],
            [
              'match',
              ['get', 'class'],
              'motorway',
              'hsl(30, 88%, 64%)',
              'trunk',
              'hsl(50, 81%, 54%)',
              'hsl(20, 18%, 100%)',
            ],
            9,
            [
              'match',
              ['get', 'class'],
              'motorway',
              'hsl(30, 100%, 70%)',
              'hsl(50, 89%, 70%)',
            ],
          ],
          'line-opacity': ['interpolate', ['linear'], ['zoom'], 3, 0, 3.5, 1],
        },
        'source-layer': 'road',
      });
    }
  }

  addModelLayers(
    model: string,
    parameter: string,
    regionName: string,
    run: Moment,
    selectedTimeStep
  ): void {
    this.setModelParameters(
      model,
      parameter,
      regionName,
      run,
      selectedTimeStep
    );

    this.addCountryLayers();
    this.modelLayers = [];
    const modelImages = [];
    for (const runTime of this.runData[run.toISOString()]) {
      const runDtg = run.utc().format('YYYYMMDDHH');
      const modelImage =
        this.dataUrl + `/maps/${model}/${runDtg}/${runTime.item}.webp`;
      modelImages.push(modelImage);
    }
    for (let i = 0; i < modelImages.length; i++) {
      this.map.addSource('model-' + i, {
        url: modelImages[i],
        coordinates: this.regions[regionName].coordinates,
        type: 'image',
      });
      const modelLayer: mapboxgl.AnyLayer = {
        id: 'model-' + i,
        source: 'model-' + i,
        type: 'raster',
        paint: {
          'raster-opacity': 0.8,
          'raster-fade-duration': 0,
          'raster-opacity-transition': { delay: 0, duration: 0 },
        },
        layout: {
          visibility: this.isLayerActive(i, selectedTimeStep)
            ? 'visible'
            : 'none',
        },
      };
      this.map.addLayer(modelLayer, 'boundary-layer');
      this.modelLayers.push(modelLayer);
    }
    this.setTimeStampControl(0);
  }

  isLayerActive(i: number, selectedTimeStep: Moment): boolean {
    return selectedTimeStep.isSame(
      moment(this.runData[this.selectedRun.toISOString()][i].dtg)
    );
  }

  removeModelLayers(): void {
    for (const modelLayer of this.modelLayers) {
      this.map.removeLayer(modelLayer.id);
      this.map.removeSource(modelLayer.id);
    }
    this.modelLayers = [];
  }

  flyToRegion(regionName: string) {
    const bounds = this.getBounds(regionName);
    this.map.setMaxBounds(bounds);
    this.map.fitBounds(bounds, {
      padding: {
        top: this.regions[regionName].padding,
        bottom: this.regions[regionName].padding,
        left: this.regions[regionName].padding,
        right: this.regions[regionName].padding,
      },
    });
  }

  flyToZoomPoint(latitude: number, longitude: number, zoom = 8) {
    this.map.flyTo({ center: [longitude, latitude], zoom });
  }

  setTimeStep(index: number): void {
    if (!this.map) {
      return;
    }
    for (
      let i = 0;
      i < this.runData[this.selectedRun.toISOString()].length;
      i++
    ) {
      const layer = 'model-' + i;
      if (this.map.getLayer(layer) !== undefined) {
        this.map.setLayoutProperty(
          layer,
          'visibility',
          i === index ? 'visible' : 'none'
        );
      }
    }
    this.setTimeStampControl(index);
  }

  setTimeStampControl(index: number): void {
    if (this.timeStampControl) {
      this.map.removeControl(this.timeStampControl);
    }
    const text = moment(
      this.runData[this.selectedRun.toISOString()][index].dtg
    ).format('dd DD.MM. HH:mm');
    this.timeStampControl = new TimeStampControl(text);
    this.map.addControl(this.timeStampControl, 'top-left');
  }

  getRunTimes(
    model: string,
    param: string,
    startDtg: string,
    endDtg: string
  ): Observable<any> {
    const modelId = this.modelsId[model];
    const url = 'https://mapserver-api.metops.net/state/shapefile/runs';
    const fullUrl = `${url}?type=${modelId}&param=${param}&validStart=${startDtg}&validEnd=${endDtg}`;
    return this.http
      .get(fullUrl, { headers: { Authorization: 'Bearer -' } })
      .pipe(
        map(data => {
          return data;
        })
      );
  }

  private setSelectLayerControl() {
    this.map.addControl(
      new CustomIconControl(
        ['far', 'layer-group'],
        'click',
        onSelectLayer.bind(
          this,
          this.dialog,
          this.map,
          this.localStorageId,
          () => {
            this.removeModelLayers();
          },
          () => {
            this.addModelLayers(
              this.model,
              this.parameter,
              this.regionName,
              this.run,
              this.selectedTimeStep
            );
          }
        )
      )
    );
  }

  private setModelParameters(
    model: string,
    parameter: string,
    regionName: string,
    run: Moment,
    selectedTimeStep: Moment
  ) {
    this.model = model;
    this.parameter = parameter;
    this.regionName = regionName;
    this.run = run;
    this.selectedTimeStep = selectedTimeStep;
  }

  private getBounds(regionName: string) {
    const region = this.regions[regionName];

    const lons = region.coordinates.map(c => c[0]);
    const lats = region.coordinates.map(c => c[1]);

    const west = Math.min(...lons);
    const east = Math.max(...lons);
    const south = Math.min(...lats);
    const north = Math.max(...lats);

    const clampedSouth = Math.max(
      -MAX_MERCATOR_LAT,
      Math.min(MAX_MERCATOR_LAT, south)
    );
    const clampedNorth = Math.max(
      -MAX_MERCATOR_LAT,
      Math.min(MAX_MERCATOR_LAT, north)
    );

    return new mapboxgl.LngLatBounds(
      [west, clampedSouth],
      [east, clampedNorth]
    );
  }

  /*
  private setSpanishRegionPointsByClient() {
    const customerDocument = this.auth.getCustomerDocument();
    switch (customerDocument.customerId) {
      case Customers.Navarra:
      case Customers.Pirineo:
      case Customers.Audenasa:
        this.regionPoints = this.regionPoints.map(region => {
          if (region.name === 'es') {
            return {
              name: 'es',
              points: [
                {
                  latitude: 42.818454,
                  longitude: -1.644256,
                  searchName: 'Pamplona',
                  zoom: 7.2,
                },
              ],
            };
          }
          return region;
        });
        break;
      case Customers.Burgos:
        this.regionPoints = this.regionPoints.map(region => {
          if (region.name === 'es') {
            return {
              name: 'es',
              points: [
                {
                  latitude: 42.35022,
                  longitude: -3.67527,
                  searchName: 'Burgos',
                  zoom: 7,
                },
              ],
            };
          }
          return region;
        });
        break;
      case Customers.Galicia:
        this.regionPoints = this.regionPoints.map(region => {
          if (region.name === 'es') {
            return {
              name: 'es',
              points: [
                {
                  latitude: 42.793827,
                  longitude: -7.880405,
                  searchName: 'Galicia',
                  zoom: 7,
                },
              ],
            };
          }
          return region;
        });
        break;
      case Customers.Aucalsa:
        this.regionPoints = this.regionPoints.map(region => {
          if (region.name === 'es') {
            return {
              name: 'es',
              points: [
                {
                  latitude: 43.018761,
                  longitude: -5.819307,
                  searchName: 'Aucalsa',
                },
              ],
            };
          }
          return region;
        });
        break;
    }
  } */
}

class TimeStampControl {
  map: mapboxgl.Map;
  container: HTMLDivElement;
  text: string;

  constructor(text: string) {
    this.text = text;
  }

  onAdd(map): HTMLDivElement {
    this.map = map;
    this.container = document.createElement('div');
    this.container.className = 'dtg-control';
    this.container.textContent = this.text;
    return this.container;
  }

  onRemove(): void {
    if (this.container) {
      this.container.parentNode.removeChild(this.container);
    }
    this.map = undefined;
  }
}

export type ModelData = Array<SingleModelDataItem>;

export interface SingleModelDataItem {
  dtg: string;
  url: string;
}
